import { clsx } from 'clsx';

export interface EmptyStateProps {
  title: string;
  description: string;
  hint?: string;
  icon?: 'inbox' | 'chart' | 'people' | 'search' | 'rocket' | 'shield';
  action?: { label: string; onClick: () => void };
  compact?: boolean;
}

function IllustrationInbox({ size }: { size: number }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 64 64" fill="none">
      <rect x="8" y="12" width="48" height="40" rx="6" stroke="var(--primary)" strokeWidth="2" opacity="0.2" />
      <rect x="12" y="16" width="40" height="32" rx="4" stroke="var(--primary)" strokeWidth="1.5" opacity="0.4" />
      <path d="M12 36h12l4 4h8l4-4h12" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M24 26h16" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
      <path d="M28 22h8" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" opacity="0.3" />
      <circle cx="32" cy="32" r="28" stroke="var(--primary)" strokeWidth="1" opacity="0.08" strokeDasharray="4 4" />
    </svg>
  );
}

function IllustrationChart({ size }: { size: number }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="28" stroke="var(--primary)" strokeWidth="1" opacity="0.08" strokeDasharray="4 4" />
      <rect x="12" y="36" width="8" height="16" rx="2" fill="var(--primary)" opacity="0.15" />
      <rect x="24" y="26" width="8" height="26" rx="2" fill="var(--primary)" opacity="0.25" />
      <rect x="36" y="18" width="8" height="34" rx="2" fill="var(--primary)" opacity="0.35" />
      <rect x="48" y="30" width="8" height="22" rx="2" fill="var(--primary)" opacity="0.2" />
      <path d="M14 34l12-10 12 6 12-14" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.6" />
      <circle cx="14" cy="34" r="2.5" fill="var(--primary)" opacity="0.5" />
      <circle cx="26" cy="24" r="2.5" fill="var(--primary)" opacity="0.5" />
      <circle cx="38" cy="30" r="2.5" fill="var(--primary)" opacity="0.5" />
      <circle cx="50" cy="16" r="2.5" fill="var(--primary)" opacity="0.5" />
    </svg>
  );
}

function IllustrationPeople({ size }: { size: number }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="28" stroke="var(--primary)" strokeWidth="1" opacity="0.08" strokeDasharray="4 4" />
      <circle cx="32" cy="22" r="7" stroke="var(--primary)" strokeWidth="2" opacity="0.5" />
      <path d="M20 46c0-6.627 5.373-12 12-12s12 5.373 12 12" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" opacity="0.4" />
      <circle cx="16" cy="26" r="4.5" stroke="var(--primary)" strokeWidth="1.5" opacity="0.25" />
      <path d="M8 42c0-4.418 3.582-8 8-8" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" opacity="0.2" />
      <circle cx="48" cy="26" r="4.5" stroke="var(--primary)" strokeWidth="1.5" opacity="0.25" />
      <path d="M56 42c0-4.418-3.582-8-8-8" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" opacity="0.2" />
    </svg>
  );
}

function IllustrationSearch({ size }: { size: number }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="28" stroke="var(--primary)" strokeWidth="1" opacity="0.08" strokeDasharray="4 4" />
      <circle cx="28" cy="28" r="12" stroke="var(--primary)" strokeWidth="2" opacity="0.4" />
      <circle cx="28" cy="28" r="7" stroke="var(--primary)" strokeWidth="1.5" opacity="0.2" />
      <path d="M37 37l10 10" stroke="var(--primary)" strokeWidth="3" strokeLinecap="round" opacity="0.5" />
      <path d="M24 24l8 0" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
      <path d="M28 20v8" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
    </svg>
  );
}

function IllustrationRocket({ size }: { size: number }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="28" stroke="var(--primary)" strokeWidth="1" opacity="0.08" strokeDasharray="4 4" />
      <path d="M32 12c0 0-14 10-14 28h28c0-18-14-28-14-28z" stroke="var(--primary)" strokeWidth="2" opacity="0.4" fill="var(--primary)" fillOpacity="0.06" />
      <circle cx="32" cy="28" r="4" stroke="var(--primary)" strokeWidth="1.5" opacity="0.5" />
      <path d="M22 44l-4 8" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" opacity="0.3" />
      <path d="M42 44l4 8" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" opacity="0.3" />
      <path d="M28 52h8" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" opacity="0.3" />
      <circle cx="28" cy="52" r="1.5" fill="var(--primary)" opacity="0.2" />
      <circle cx="36" cy="52" r="1.5" fill="var(--primary)" opacity="0.2" />
    </svg>
  );
}

function IllustrationShield({ size }: { size: number }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="28" stroke="var(--primary)" strokeWidth="1" opacity="0.08" strokeDasharray="4 4" />
      <path d="M32 8l18 8v16c0 12-8 20-18 24C22 52 14 44 14 32V16l18-8z" stroke="var(--primary)" strokeWidth="2" opacity="0.3" fill="var(--primary)" fillOpacity="0.04" />
      <path d="M32 14l12 5.5v11c0 8.5-5.5 14-12 17" stroke="var(--primary)" strokeWidth="1.5" opacity="0.15" />
      <path d="M26 32l4 4 8-8" stroke="var(--primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.6" />
    </svg>
  );
}

const ILLUSTRATIONS: Record<string, (props: { size: number }) => JSX.Element> = {
  inbox: IllustrationInbox,
  chart: IllustrationChart,
  people: IllustrationPeople,
  search: IllustrationSearch,
  rocket: IllustrationRocket,
  shield: IllustrationShield,
};

export function EmptyState({ title, description, hint, icon = 'inbox', action, compact = false }: EmptyStateProps) {
  const Illustration = ILLUSTRATIONS[icon] ?? ILLUSTRATIONS['inbox']!;
  const size = compact ? 48 : 72;

  return (
    <div className={clsx('flex flex-col items-center justify-center text-center', compact ? 'py-4' : 'py-8')}>
      <div className="mb-4">
        <Illustration size={size} />
      </div>
      <div className={clsx('font-semibold text-ink-muted', compact ? 'text-xs' : 'text-sm')}>{title}</div>
      <div className={clsx('mt-1 max-w-xs text-ink-dim leading-relaxed', compact ? 'text-2xs' : 'text-xs')}>{description}</div>
      {hint && (
        <div className={clsx('mt-2 max-w-xs rounded-lg border border-dashed border-border px-3 py-1.5 text-ink-dim/70 leading-relaxed', compact ? 'text-[10px]' : 'text-2xs')}>
          <span className="font-medium text-ink-dim">Once connected:</span> {hint}
        </div>
      )}
      {action && (
        <button
          onClick={action.onClick}
          className="mt-3 rounded-lg bg-primary px-4 py-1.5 text-2xs font-medium text-white hover:bg-primary/90 transition-colors"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
